import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiActivity,
  FiArrowRight,
  FiCompass,
  FiCpu,
  FiGrid,
  FiLink2,
  FiPieChart,
  FiShield,
  FiTrendingUp,
} from 'react-icons/fi';
import './home-page.css';

const primaryActions = [
  {
    title: 'Dashboard',
    description: 'Vue tactique des signaux, du risque et des priorités du moment.',
    path: '/dashboard',
    Icon: FiGrid,
    tone: 'cyan',
  },
  {
    title: 'Pattern Scanner',
    description: 'Repérez les structures multi-timeframe qui méritent vraiment de l’attention.',
    path: '/scanner',
    Icon: FiCompass,
    tone: 'copper',
  },
  {
    title: 'Portefeuille',
    description: 'Regard consolidé sur la valorisation, l’exposition et l’alignement macro.',
    path: '/unified-portfolio',
    Icon: FiPieChart,
    tone: 'green',
  },
  {
    title: 'Marché',
    description: 'Lecture du régime, de la dominance et de la participation marché.',
    path: '/market',
    Icon: FiTrendingUp,
    tone: 'blue',
  },
  {
    title: 'Asset Scanner',
    description: 'Filtrez l’univers crypto pour isoler les actifs exploitables plus vite.',
    path: '/asset-scanner',
    Icon: FiActivity,
    tone: 'violet',
  },
  {
    title: 'Connexions',
    description: 'Pilotez vos exchanges et wallets sans sortir du cockpit.',
    path: '/connections',
    Icon: FiLink2,
    tone: 'slate',
  },
];

const workflowSteps = [
  {
    title: 'Lire le contexte',
    description: 'Commencez par le régime marché, la dominance et le sentiment pour cadrer le terrain.',
    Icon: FiTrendingUp,
  },
  {
    title: 'Isoler les signaux',
    description: 'Passez du bruit à une short-list d’actifs et de patterns réellement actionnables.',
    Icon: FiCompass,
  },
  {
    title: 'Arbitrer le risque',
    description: 'Recoupez portefeuille, alignement macro et exécution avant de monter en taille.',
    Icon: FiShield,
  },
];

const stackHighlights = [
  'Régime et breadth marché consolidés',
  'Scanner asset et pattern dans le même cockpit',
  'Portefeuille relié au contexte macro réel',
];

const regimeLabels = {
  ALTSEASON: 'Altseason',
  BTC_DOMINANCE_UP: 'BTC dominant',
  BITCOIN_DOMINANT: 'BTC dominant',
  DEFENSIVE: 'Défensif',
  DEFENSIF: 'Défensif',
  ETH_ROTATION: 'Rotation ETH',
  NEUTRAL: 'Neutre',
  NEUTRE: 'Neutre',
  ROTATION_ETH: 'Rotation ETH',
};

function fetchJson(url, signal) {
  return fetch(url, { signal }).then((response) => {
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  });
}

function formatCompactNumber(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return new Intl.NumberFormat('fr-FR', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numericValue);
}

function formatUsdCompact(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numericValue);
}

function formatPercent(value, digits = 1) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return `${numericValue.toFixed(digits)}%`;
}

function formatScore(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  const normalizedValue = Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
  return `${Math.round(normalizedValue)}/100`;
}

function formatRelativeDate(value) {
  if (!value) {
    return 'En attente de synchronisation';
  }
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'En attente de synchronisation';
  }
  return parsedDate.toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function humanizeRegime(regime) {
  if (!regime) {
    return 'Lecture en attente';
  }
  return regimeLabels[regime] || regime;
}

function HomePage() {
  const [marketMetrics, setMarketMetrics] = useState(null);
  const [portfolioOverview, setPortfolioOverview] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    async function loadPulse() {
      const [marketResult, portfolioResult] = await Promise.allSettled([
        fetchJson('/api/market/metrics', controller.signal),
        fetchJson('/api/portfolio', controller.signal),
      ]);

      if (!active) {
        return;
      }

      if (marketResult.status === 'fulfilled') {
        setMarketMetrics(marketResult.value);
      }
      if (portfolioResult.status === 'fulfilled') {
        setPortfolioOverview(portfolioResult.value);
      }
    }

    loadPulse();

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const heroPulse = [
    {
      label: 'Régime marché',
      value: humanizeRegime(marketMetrics?.regime),
      detail: marketMetrics?.riskOnOff || 'Qualification automatique du terrain',
    },
    {
      label: 'Macro score',
      value: formatScore(marketMetrics?.macroScore),
      detail: `BTC ${formatPercent(marketMetrics?.btcDominance, 1)}`,
    },
    {
      label: 'Sentiment',
      value: formatScore(marketMetrics?.sentiment),
      detail: `Capi ${formatUsdCompact(marketMetrics?.marketCap)}`,
    },
    {
      label: 'Portefeuille',
      value: formatCompactNumber(portfolioOverview?.portfolioValue),
      detail: portfolioOverview?.assetCount != null ? `${portfolioOverview.assetCount} actifs suivis` : 'Vue consolidée prête',
    },
  ];

  const marketSummary = [
    {
      label: 'Dominance BTC',
      value: formatPercent(marketMetrics?.btcDominance, 2),
    },
    {
      label: 'Variation 24h',
      value: formatPercent(marketMetrics?.marketCap24hChange, 2),
    },
    {
      label: 'Confiance régime',
      value: formatScore(marketMetrics?.regimeConfidence),
    },
  ];

  return (
    <div className="home-page">
      <div className="home-page__shell">
        <section className="home-hero">
          <div className="home-hero__copy">
            <div className="home-badge">
              <span className="home-badge__dot" />
              EcotoneAI, cockpit de décision crypto
            </div>
            <h1 className="home-hero__title">
              Passez du bruit du marché à des décisions plus lisibles.
            </h1>
            <p className="home-hero__subtitle">
              EcotoneAI relie le contexte macro, le scanning d’opportunités et le portefeuille dans un même flux de travail.
              L’idée reste fidèle au mot ecotone : une zone de transition où deux mondes se rencontrent, sauf qu’ici le passage se fait entre données brutes et signaux exploitables.
            </p>

            <div className="home-hero__actions">
              <Link to="/dashboard" className="home-cta home-cta--primary">
                Ouvrir le dashboard
                <FiArrowRight />
              </Link>
              <Link to="/scanner" className="home-cta home-cta--secondary">
                Lancer le pattern scanner
              </Link>
              <Link to="/market" className="home-cta home-cta--ghost">
                Lire le marché
              </Link>
            </div>

            <div className="home-stack-list">
              {stackHighlights.map((item) => (
                <div key={item} className="home-stack-list__item">
                  <FiCpu />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="home-hero__panel">
            <div className="home-panel home-panel--primary">
              <div className="home-panel__header">
                <span className="home-panel__eyebrow">Pulse live</span>
                <span className="home-panel__timestamp">{formatRelativeDate(marketMetrics?.lastUpdate || marketMetrics?.timestamp)}</span>
              </div>
              <div className="home-pulse-grid">
                {heroPulse.map((item) => (
                  <div key={item.label} className="home-pulse-card">
                    <span className="home-pulse-card__label">{item.label}</span>
                    <strong className="home-pulse-card__value">{item.value}</strong>
                    <span className="home-pulse-card__detail">{item.detail}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="home-panel home-panel--secondary">
              <div className="home-panel__header">
                <span className="home-panel__eyebrow">Lecture du terrain</span>
                <span className="home-panel__status">{marketMetrics ? 'Données connectées' : 'Connexion en cours'}</span>
              </div>
              <div className="home-summary-strip">
                {marketSummary.map((item) => (
                  <div key={item.label} className="home-summary-strip__item">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
              <p className="home-panel__insight">
                {portfolioOverview?.topRisk?.title || 'La page d\'accueil est prête pour servir de point d’entrée opérationnel, même si certaines métriques live n’ont pas encore répondu.'}
              </p>
            </div>
          </div>
        </section>

        <section className="home-section">
          <div className="home-section__heading">
            <div>
              <span className="home-section__eyebrow">Accès direct</span>
              <h2>Les modules qui comptent vraiment au quotidien</h2>
            </div>
            <p>
              La page d’accueil doit vous faire entrer dans le bon écran en quelques secondes, pas vous laisser devant un slogan vide.
            </p>
          </div>

          <div className="home-feature-grid">
            {primaryActions.map(({ title, description, path, Icon, tone }) => (
              <Link to={path} key={title} className={`home-feature-card home-feature-card--${tone}`}>
                <div className="home-feature-card__top">
                  <div className="home-feature-card__icon">
                    <Icon />
                  </div>
                  <span className="home-feature-card__link">
                    Ouvrir
                    <FiArrowRight />
                  </span>
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
              </Link>
            ))}
          </div>
        </section>

        <section className="home-section home-section--workflow">
          <div className="home-section__heading">
            <div>
              <span className="home-section__eyebrow">Méthode</span>
              <h2>Un flux pensé pour arbitrer, pas pour empiler des widgets</h2>
            </div>
            <p>
              La séquence utile est simple : contexte, détection, exécution. La home sert de rampe d’accès à ce trio.
            </p>
          </div>

          <div className="home-workflow-grid">
            {workflowSteps.map(({ title, description, Icon }, index) => (
              <div key={title} className="home-workflow-card">
                <div className="home-workflow-card__step">0{index + 1}</div>
                <div className="home-workflow-card__icon">
                  <Icon />
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default HomePage;